declare const DEV_DOMAIN: string | undefined;
declare const OVERRIDE_APP_URL_DOMAIN: string;
declare const OVERRIDE_CHAMPAIGN_URL_DOMAIN: string;
declare const OVERRIDE_CANVAS_IFRAME_URL_DOMAIN: string;

function localOffsetSuffix() {
  if (typeof window === 'undefined') {
    return '';
  }

  const [subdomain] = window.location.hostname.split('.');
  const match = subdomain?.match(/-(\d+)$/);
  return match ? `-${match[1]}` : '';
}

function withLocalOffset(hostname: string) {
  const suffix = localOffsetSuffix();
  if (!suffix) {
    return hostname;
  }

  return hostname.replace(/^app(?=\.)/, `app${suffix}`);
}

function localDevUrlWithSubdomain(subdomain: string) {
  if (typeof window === 'undefined') {
    return '';
  }

  const { hostname, port } = window.location;
  const hostParts = hostname.split('.');
  const suffix = localOffsetSuffix();
  const nextSubdomain = `${subdomain}${suffix}`;

  if (hostParts.length === 2) {
    return `//${nextSubdomain}.${hostname}${port ? `:${port}` : ''}`;
  }

  hostParts[0] = nextSubdomain;
  return `//${hostParts.join('.')}${port ? `:${port}` : ''}`;
}

function urlWithSubdomain(subdomain: string) {
  const { host } = window.location;
  const splitHost = host.split('.');

  if (splitHost.length === 2) {
    // No subdomain
    return `//${subdomain}.${splitHost.join('.')}`;
  }
  // With subdomain
  splitHost[0] = subdomain;
  return `//${splitHost.join('.')}`;
}

const devDomain = () => (typeof DEV_DOMAIN !== 'undefined' && DEV_DOMAIN !== '' ? DEV_DOMAIN : 'mutiny.local');
const appUrlDomainOverride = () =>
  typeof OVERRIDE_APP_URL_DOMAIN !== 'undefined' && OVERRIDE_APP_URL_DOMAIN !== '' ? OVERRIDE_APP_URL_DOMAIN : '';
const champaignUrlDomainOverride = () =>
  typeof OVERRIDE_CHAMPAIGN_URL_DOMAIN !== 'undefined' && OVERRIDE_CHAMPAIGN_URL_DOMAIN !== ''
    ? OVERRIDE_CHAMPAIGN_URL_DOMAIN
    : '';
const canvasIframeUrlDomainOverride = () =>
  typeof OVERRIDE_CANVAS_IFRAME_URL_DOMAIN !== 'undefined' && OVERRIDE_CANVAS_IFRAME_URL_DOMAIN !== ''
    ? OVERRIDE_CANVAS_IFRAME_URL_DOMAIN
    : '';

type AppStage = 'development' | 'staging' | 'production' | 'test';

// RC builds run at `{rc-name}.rc.mutinyhq-staging.com` and share the staging
// backend, so `getAppStage()` returns `'staging'` for them. The canvas iframe
// is the one place we need to distinguish RC: it ships in the per-RC bucket
// and loads from a paired `{rc-name}.rc.peak-achoo-staging.com` host so the
// iframe is cross-origin to the dashboard, mirroring the prod/staging split.
const isRcHost = () => typeof window !== 'undefined' && window.location.hostname.includes('.rc.mutinyhq-staging.com');

let cachedAppStage: AppStage | null = null;

function appStageFromHostname(hostname: string): AppStage | null {
  if (hostname.includes('mutinyhq-staging.com')) return 'staging';
  if (hostname.includes('mutinyhq.com')) return 'production';
  return null;
}

export function getAppStage(): AppStage {
  if (cachedAppStage !== null) {
    return cachedAppStage;
  }

  if (typeof window === 'undefined') {
    // Don't cache the SSR fallback — window may exist on subsequent calls after hydration.
    return 'development';
  }

  let stage = appStageFromHostname(window.location.hostname);

  // Canvas iframe (`canvas.peak-achoo*`) carries no stage signal; read it from `parentOrigin`.
  if (stage === null) {
    const parentOrigin = new URLSearchParams(window.location.search).get('parentOrigin');
    if (parentOrigin) {
      try {
        stage = appStageFromHostname(new URL(parentOrigin).hostname);
      } catch {
        // Malformed parentOrigin — fall through to 'development'.
      }
    }
  }

  cachedAppStage = stage ?? 'development';
  return cachedAppStage;
}

export const adminUrl = () => {
  switch (getAppStage()) {
    case 'production':
      return 'https://admin.mutinyhq.com';
    case 'staging':
      return 'https://admin.mutinyhq-staging.com';
    default:
      // Match the dashboard's local slot: app-1.mutiny.local → admin-1.mutiny.local, app → admin.
      return `https://admin${localOffsetSuffix()}.${devDomain()}`;
  }
};

export const wwwUrl = () => urlWithSubdomain('www');
export const previewUrl = () => urlWithSubdomain('preview');
export const appUrl = () => urlWithSubdomain('app');
export const proxyUrl = () => {
  switch (getAppStage()) {
    case 'production':
      return 'https://peek-achoo.com';
    case 'staging':
      return 'https://peek-achoo-staging.com';
    default:
      return 'https://peek-achoo.local';
  }
};
export const appUrlDomain = () => {
  const appStage = getAppStage();
  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  if (['development', 'staging'].includes(appStage) && searchParams.has('mutiny_app_url')) {
    const override = searchParams.get('mutiny_app_url');
    if (!override) {
      return override;
    }

    if (appStage === 'development') {
      const normalized = override.replace(/^https?:\/\//, '//');
      if (normalized.startsWith('//')) {
        const host = normalized.slice(2);
        return `//${withLocalOffset(host)}`;
      }
    }

    return override;
  }

  const overrideAppUrlDomain = appUrlDomainOverride();
  // The compile-time override must win over the window-derived local host:
  // extension pages have a dotless chrome-extension hostname, which the
  // subdomain rewrite would otherwise turn into the bogus host "//app".
  if (appStage === 'development' && overrideAppUrlDomain) {
    return `//${overrideAppUrlDomain}`;
  }

  if (appStage === 'development') {
    const localAppHost = localDevUrlWithSubdomain('app');
    if (localAppHost) {
      return localAppHost;
    }
  }

  switch (appStage) {
    case 'production':
      return '//app.mutinyhq.com';
    case 'staging':
      return '//app.mutinyhq-staging.com';
    default:
      return `//app${localOffsetSuffix()}.${devDomain()}`;
  }
};

export const champaignUrlDomain = () => {
  const appStage = getAppStage();
  const localChampaignHost =
    localDevUrlWithSubdomain('champaign') || `//champaign${localOffsetSuffix()}.${devDomain()}`;

  switch (appStage) {
    case 'production':
      return '//campaigns.mutinyhq.com';
    case 'staging':
      return '//campaigns.mutinyhq-staging.com';
    default:
      return champaignUrlDomainOverride() ? `//${champaignUrlDomainOverride()}` : localChampaignHost;
  }
};

export const canvasIframeUrlDomain = () => {
  if (isRcHost()) {
    // Each RC pairs with a matching `{rc-name}.rc.peak-achoo-staging.com`
    // canvas host so the iframe is cross-origin to the RC dashboard.
    const rcName = window.location.hostname.split('.')[0];
    return `//${rcName}.rc.peak-achoo-staging.com`;
  }
  const appStage = getAppStage();
  const localCanvasHost = localDevUrlWithSubdomain('canvas') || `//canvas${localOffsetSuffix()}.${devDomain()}`;

  switch (appStage) {
    case 'production':
      return '//canvas.peak-achoo.com';
    case 'staging':
      return '//canvas.peak-achoo-staging.com';
    default:
      return canvasIframeUrlDomainOverride() ? `//${canvasIframeUrlDomainOverride()}` : localCanvasHost;
  }
};

// Cross-origin postMessage trust list. The canvas iframe reads its parent's origin
// from a URL param (attacker-controllable in principle), so it must validate
// against the dashboard origins this build knows about before mounting the bridge.
// Key off the canvas host we're served from — getAppStage() reads `*.mutinyhq*`
// hostnames and falls through to 'development' inside the canvas iframe, which
// would reject every real-world parent.
export const isAllowedParentOrigin = (origin: string): boolean => {
  if (typeof window === 'undefined') return false;
  // RC canvas runs at `{rc-name}.rc.peak-achoo-staging.com` and only trusts the
  // matching `{rc-name}.rc.mutinyhq-staging.com` dashboard, so a leaked RC name
  // can't be embedded by an unrelated RC dashboard.
  if (window.location.hostname.endsWith('.rc.peak-achoo-staging.com')) {
    const rcName = window.location.hostname.split('.')[0];
    return origin === `https://${rcName}.rc.mutinyhq-staging.com`;
  }
  switch (window.location.host) {
    case 'canvas.peak-achoo.com':
      return origin === 'https://app.mutinyhq.com';
    case 'canvas.peak-achoo-staging.com':
      return origin === 'https://app.mutinyhq-staging.com';
    default:
      return origin === `${window.location.protocol}${appUrlDomain()}`;
  }
};

export const agentUrlDomain = () => {
  switch (getAppStage()) {
    case 'production':
      return '//agent.mutinyhq.com';
    case 'staging':
      return '//agent.mutinyhq-staging.com';
    default:
      return champaignUrlDomain();
  }
};

export const mcpGatewayUrl = () => {
  switch (getAppStage()) {
    case 'production':
      return 'https://mcp.mutinyhq.com/mcp';
    case 'staging':
    default:
      return 'https://mcp.mutinyhq-staging.com/mcp';
  }
};
